import { useState, useRef, useEffect, useCallback } from 'react';
import { ApiClient } from '../api/client';
import { CREATION_TYPES } from '../config/creationTypes';
import { useNavigate } from 'react-router-dom';
import { BestMomentsCreationConfig, BestMomentsConfigRequestData } from '../components/strategies/BestMomentsCreationConfig';
import { UserInfo } from '../types';
import { StrategyType } from '../constants/strategy';
import posthog from 'posthog-js';
import { useTranslation } from '../i18n';
import { useCreationTypeVideo } from '../hooks/useLocalizedVideo';

interface UploadVideoPageProps {
  creationTypeName: string;
  onUploadComplete: (fileId: string, autoProcess?: boolean) => void;
  isPro?: boolean;
  isComingSoon?: boolean;
  onUpgradeClick?: () => void;
  userInfo?: UserInfo;
  creationId?: string;
  strategyType?: StrategyType;
  initialConfig?: BestMomentsConfigRequestData;
  onConfigChange?: (config: BestMomentsConfigRequestData) => void;
}

export function UploadVideoPage({ 
  creationTypeName, 
  onUploadComplete,
  isPro = false,
  isComingSoon = false,
  onUpgradeClick,
  userInfo,
  creationId,
  strategyType,
  initialConfig,
  onConfigChange
}: UploadVideoPageProps) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [showUploadScreen, setShowUploadScreen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [autoStartProcessing, setAutoStartProcessing] = useState(false);
  const [bestMomentsConfig, setBestMomentsConfig] = useState<BestMomentsConfigRequestData | null>(
    initialConfig || null
  );
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const [isPriceFetching, setIsPriceFetching] = useState(false);
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  
  const navigate = useNavigate();
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewSectionRef = useRef<HTMLDivElement>(null);
  const uploadSectionRef = useRef<HTMLDivElement>(null);
  
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Find the creation type from the centralized config
  const creationType = CREATION_TYPES.find(type => type.id === creationTypeName);
  
  // Get the video path for this creation type based on current language
  const videoPath = useCreationTypeVideo(creationTypeName);
  
  // Get the creation type header and description from translations
  const creationTypeHeader = t(`create.types.${creationTypeName}.header`, creationTypeName);
  const creationTypeDescription = t(`create.types.${creationTypeName}.description`, '');

  const fetchCalculatedPrice = useCallback(async () => {
    if (!creationId || isPriceFetching) return;

    setIsPriceFetching(true);
    try {
      const data = await ApiClient.getCreationPrice(creationId);
      setCalculatedPrice(data.price);
    } catch (error) {
      console.error('Error fetching price:', error);
    } finally {
      setIsPriceFetching(false);
    }
  }, [creationId, isPriceFetching]);

  // Fetch calculated price for best moments if we have a creation ID
  useEffect(() => {
    if (strategyType === StrategyType.BEST_MOMENTS && creationId && !calculatedPrice && !isPriceFetching) {
      fetchCalculatedPrice();
    }
  }, [strategyType, calculatedPrice, isPriceFetching, creationId, fetchCalculatedPrice]);

  // Handle config changes
  const handleConfigChange = (config: BestMomentsConfigRequestData) => {
    setBestMomentsConfig(config);
    if (onConfigChange) {
      onConfigChange(config);
    }
  };
  
  // Remove the debug console.log in the autoStartProcessing useEffect
  useEffect(() => {
    // No need for debug logging
  }, [autoStartProcessing]);

  // Remove the debug console.log in the creationId useEffect
  useEffect(() => {
    // No need for debug logging
  }, [creationId]);

  // Clean up the file upload handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check if file is a video
    if (!file.type.startsWith('video/')) {
      setUploadError(t('errors.invalidFileType'));
      e.target.value = '';
      return;
    }
    
    setIsUploading(true);
    setUploadError('');
    
    try {
      // Upload the file
      const response = await ApiClient.uploadFile(file, (progress) => {
        setUploadProgress(progress);
      });
      
      // Store the uploaded file info
      setUploadedFileId(response.id);
      setUploadedFileName(file.name);
      
      // PostHog: Capture file uploaded event
      posthog.capture('file_uploaded', { file_id: response.id, file_name: file.name, creation_type: creationTypeName });
      
      // Pass the file ID to parent component 
      onUploadComplete(response.id);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError(t('errors.fileUpload'));
      e.target.value = '';
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Clean up the handleDrop function
  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    
    // Check if file is a video
    if (!file.type.startsWith('video/')) {
      setUploadError(t('errors.invalidFileType'));
      return;
    }
    
    setIsUploading(true);
    setUploadError('');
    
    try {
      // Upload the file
      const response = await ApiClient.uploadFile(file, (progress) => {
        setUploadProgress(progress);
      });
      
      // Store the uploaded file info
      setUploadedFileId(response.id);
      setUploadedFileName(file.name);
      
      // PostHog: Capture file uploaded event
      posthog.capture('file_uploaded', { file_id: response.id, file_name: file.name, creation_type: creationTypeName });
      
      // Pass the file ID to parent component
      onUploadComplete(response.id);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError(t('errors.fileUpload'));
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Clean up the auto-processing useEffect
  useEffect(() => {
    // Only run auto-processing when we have all required data
    if (
      !isUploading && // Upload is complete
      uploadedFileId && // We have an uploaded file
      creationId && // We have a creation ID
      autoStartProcessing && // Auto-processing is enabled
      userInfo && // We have user info
      bestMomentsConfig // We have config data
    ) {
      // Reset the uploaded file ID to prevent this from running more than once
      setUploadedFileId(null);
      
      // Execute auto-processing
      (async () => {
        try {
          // Get price
          const priceData = await ApiClient.getCreationPrice(creationId);
          const price = priceData.price;
          
          // Check balance
          if (userInfo.balance >= price) {
            // Configure
            await ApiClient.configureBestMoments(creationId, {
              captions_enabled: bestMomentsConfig.captions_enabled,
              caption_config: bestMomentsConfig.caption_config
            });
            
            // Start processing
            await ApiClient.startProcessing(creationId);
            
            // PostHog: Capture processing started event
            posthog.capture('creation_started', { 
              creation_id: creationId, 
              strategy_type: strategyType
            });
            
            // Navigate to details page
            navigate(`/creation/${creationId}`);
          }
        } catch (error) {
          console.error("Error during auto-processing:", error);
        }
      })();
    }
  }, [isUploading, uploadedFileId, creationId, autoStartProcessing, userInfo, bestMomentsConfig, navigate, strategyType]);

  // Handle transition to upload screen
  const transitionToUploadScreen = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setShowUploadScreen(true);
    
    // Allow scrolling after transition completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 800); // Match the transition duration
  }, [isTransitioning]);
  
  // Handle transition back to preview screen
  const transitionToPreviewScreen = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setShowUploadScreen(false);
    
    // Scroll to top of preview
    if (previewSectionRef.current) {
      previewSectionRef.current.scrollIntoView({ behavior: 'auto' });
    }
    
    // Allow scrolling after transition completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 800); // Match the transition duration
  }, [isTransitioning]);
  
  // Handle manual transition (button click)
  const handleManualTransition = () => {
    transitionToUploadScreen();
  };
  
  // Handle back button click in upload screen
  const handleBackToPreview = () => {
    transitionToPreviewScreen();
  };
  
  // Handle window setup and scroll detection
  useEffect(() => {
    // Set initial window height
    setWindowHeight(window.innerHeight);
    
    // Handle window resize
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };
    
    // Detect scroll to trigger transition
    const handleScroll = () => {
      // Don't handle scroll during transitions
      if (isTransitioning) {
        return;
      }
      
      // If on preview screen and scrolling down
      if (!showUploadScreen && window.scrollY > 50) {
        // Prevent default scroll behavior
        window.scrollTo(0, 0);
        
        // Trigger transition to upload
        transitionToUploadScreen();
      }
      
      // If on upload screen and scrolling up to the very top
      if (showUploadScreen && window.scrollY === 0) {
        // Only trigger if at the very top of the upload screen
        const uploadSection = uploadSectionRef.current;
        if (uploadSection && uploadSection.scrollTop === 0) {
          transitionToPreviewScreen();
        }
      }
    };
    
    // Force video to play
    const playVideo = () => {
      if (videoRef.current) {
        videoRef.current.play().catch(err => {
          console.error("Video play failed:", err);
        });
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: false });
    window.addEventListener('resize', handleResize);
    
    // Try to play the video immediately and also after a delay
    playVideo();
    setTimeout(playVideo, 1000);
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [showUploadScreen, isTransitioning, transitionToUploadScreen, transitionToPreviewScreen]);

  // Check if the user has enough balance for the best moments feature
  const hasEnoughBalance = strategyType === StrategyType.BEST_MOMENTS 
    ? (calculatedPrice === null || (userInfo && userInfo.balance >= calculatedPrice))
    : true;

  // Add back the missing event handlers
  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="fixed inset-0 bg-black text-white overflow-hidden">
      {/* Header - Always visible */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-transparent">
        <div className="px-4 py-4">
          <div className="flex justify-between items-center">
            <button 
              onClick={() => {
                if (showUploadScreen) {
                  handleBackToPreview();
                } else {
                  navigate('/');
                }
              }} 
              className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
            <h1 className="text-lg font-medium text-white">
              {creationTypeHeader}
              {isPro && (
                <span className="ml-2 bg-[#2D62FF] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  PRO
                </span>
              )}
              {isComingSoon && (
                <span className="ml-2 bg-[#8B5CF6]/80 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  SOON
                </span>
              )}
            </h1>
            <div className="p-2"></div> {/* Spacer to maintain header layout */}
          </div>
        </div>
      </div>

      {/* Hero Screen - First screen with video */}
      <div 
        ref={previewSectionRef}
        className={`fixed inset-0 z-10 flex flex-col justify-end transition-transform duration-800 ease-in-out ${
          showUploadScreen ? 'transform -translate-y-full' : ''
        }`}
        style={{ height: `${windowHeight}px` }}
      >
        {/* Video Background */}
        {videoPath && (
          <div className="absolute inset-0 w-full h-full">
            <video
              ref={videoRef}
              src={videoPath}
              autoPlay
              muted
              playsInline
              loop
              className="absolute w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-black/90 w-full h-full" />
          </div>
        )}
        
        {/* Hero Content */}
        <div className="relative z-10 px-6 pb-32">
          <div className="flex flex-col">
            <div className="flex flex-col mb-4">
              <h2 className="text-4xl font-bold mb-4">{creationTypeHeader}</h2>
              <div className="flex items-center justify-between">
                <p className="text-white/90 text-lg pr-4 max-w-[80%]">{creationTypeDescription}</p>
                {isPro ? (
                  <div className="px-3 py-1 rounded-full bg-[#2D62FF] text-white font-bold flex items-center">
                    PRO
                  </div>
                ) : isComingSoon ? (
                  <div className="px-3 py-1 rounded-full bg-[#8B5CF6]/80 text-white font-bold flex items-center">
                    SOON
                  </div>
                ) : (
                  <div className="px-3 py-1 rounded-full bg-theme-button/20 backdrop-blur-sm text-white font-bold flex items-center">
                    <span className="mr-1">✨</span> {creationType?.pricingModel || creationType?.price}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scroll/Continue Button */}
        <div className="absolute bottom-12 left-0 right-0 flex flex-col items-center">
          <button 
            onClick={handleManualTransition}
            className="flex flex-col items-center text-white focus:outline-none"
          >
            <p className="text-white/80 text-sm mb-2">
              {isPro || isComingSoon ? t('uploadVideoPage.scrollHintLearnMore') : t('uploadVideoPage.scrollHintUpload')}
            </p>
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center p-1">
              <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce-slow" />
            </div>
          </button>
        </div>
      </div>

      {/* Upload Screen - Second screen */}
      <div 
        ref={uploadSectionRef}
        tabIndex={-1}
        className={`fixed inset-0 z-20 bg-[#121217] transition-transform duration-800 ease-in-out ${
          showUploadScreen ? '' : 'transform translate-y-full'
        }`}
        style={{ height: `${windowHeight}px`, overflowY: 'auto' }}
      >
        {isPro ? (
          // Pro Feature Content
          <div className="min-h-full pt-24 pb-24 px-4">
            {/* Pro Feature Message */}
            <div className="w-full p-5 bg-black/5 dark:bg-white/5 rounded-xl border border-[#2D62FF]/20 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#2D62FF] text-white px-2 py-0.5 rounded text-sm font-bold">{t('selectCreationType.proLabel')}</span>
                <h3 className="text-lg font-medium">
                  {t('selectCreationType.proModal.featureIsPro', {
                    featureName: creationTypeName === 'podcast-series' ? 'Podcast Series' : 'Best Moments' 
                  })}
                </h3>
              </div>
              <p className="text-theme-hint mb-4">
                {t('selectCreationType.proModal.description')}
              </p>
              <button 
                onClick={onUpgradeClick}
                className="w-full bg-[#2D62FF] text-white font-medium py-3 rounded-lg hover:bg-[#2D62FF]/90 transition-colors"
              >
                {t('selectCreationType.proModal.upgradeButton')}
              </button>
            </div>

            {/* Feature Description */}
            <div className="w-full">
              <h2 className="text-xl font-medium mb-3">{t('uploadVideoPage.aboutHeader', { creationTypeHeader })}</h2>
              <p className="text-theme-hint mb-6">
                {creationTypeName === 'podcast-series' 
                  ? t('uploadVideoPage.proFeatureDescriptionPodcastSeries')
                  : t('uploadVideoPage.proFeatureDescriptionBestMoments')}
              </p>
            </div>
          </div>
        ) : isComingSoon ? (
          // Coming Soon Content
          <div className="min-h-full pt-24 pb-24 px-4">
            {/* Coming Soon Message */}
            <div className="w-full p-5 bg-black/5 dark:bg-white/5 rounded-xl border border-[#8B5CF6]/20 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#8B5CF6]/80 text-white px-2 py-0.5 rounded text-sm font-bold">{t('selectCreationType.soonLabel')}</span>
                <h3 className="text-lg font-medium">
                  {t('uploadVideoPage.comingSoonHeader', { creationTypeHeader })}
                </h3>
              </div>
              <p className="text-theme-hint mb-4">
                {t('uploadVideoPage.comingSoonDescription')}
              </p>
              <div className="w-full bg-[#8B5CF6]/10 text-[#A78BFA] font-medium py-3 rounded-lg text-center">
                <span className="material-symbols-outlined align-middle mr-2">notifications</span>
                {t('uploadVideoPage.comingSoonNotifyButton')}
              </div>
            </div>

            {/* Feature Description */}
            <div className="w-full">
              <h2 className="text-xl font-medium mb-3">{t('uploadVideoPage.aboutHeader', { creationTypeHeader })}</h2>
              <p className="text-theme-hint mb-6">
                {t('uploadVideoPage.comingSoonFeatureDescriptionAICaptions')}
              </p>
            </div>
          </div>
        ) : (
          // Regular Upload Content
          <div className="min-h-full pt-24 pb-24">
            <div className="px-4">
              <h3 className="text-xl font-medium mb-6">{t('uploadVideoPage.uploadHeader')}</h3>
              
              {uploadError && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-600 text-sm">
                  {uploadError}
                </div>
              )}
              
              <label
                htmlFor="file-upload"
                className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ${
                  isDragging ? 'border-theme-button bg-theme-button/5 scale-[1.02]' : 'border-gray-700'
                } ${isUploading ? 'opacity-90 cursor-not-allowed' : uploadedFileName ? 'border-green-500/30 bg-green-500/5' : 'hover:bg-white/5 hover:scale-[1.01]'}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  id="file-upload"
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={isUploading || uploadedFileName !== null}
                />
                
                {isUploading ? (
                  <div className="text-center">
                    <span className="material-symbols-outlined text-4xl mb-2 text-white">cloud_upload</span>
                    <p className="text-lg font-medium mb-2 text-white">{t('uploadVideoPage.uploadStatusUploading', { progress: Math.round(uploadProgress) })}</p>
                    <div className="w-full max-w-xs h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-theme-button rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                ) : uploadedFileName ? (
                  <div className="text-center">
                    <div className="bg-green-500/20 mb-4 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                      <span className="material-symbols-outlined text-4xl text-green-500">check</span>
                    </div>
                    <p className="text-lg font-medium mb-1 text-white">{t('uploadVideoPage.uploadStatusSuccess')}</p>
                    <p className="text-sm text-gray-400 mb-3 break-all max-w-sm">{uploadedFileName}</p>
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setUploadedFileName(null);
                        setUploadedFileId(null);
                      }}
                      className="text-sm bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      {t('uploadVideoPage.replaceFileButton')}
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <span className="material-symbols-outlined text-4xl mb-2 text-white">upload_file</span>
                    <p className="text-lg font-medium mb-1 text-white">{t('uploadVideoPage.dropzonePrompt')}</p>
                    <p className="text-sm text-gray-400">{t('uploadVideoPage.dropzoneHint')}</p>
                  </div>
                )}
              </label>
              
              {/* Configuration and Auto-Start Processing Section for Best Moments only */}
              {strategyType === StrategyType.BEST_MOMENTS && bestMomentsConfig && (
                <div className="mt-8">
                  {/* Price and balance info */}
                  {userInfo && (
                    <div className="mb-6 p-4 rounded-xl bg-[rgba(36,129,204,0.04)] dark:bg-[#243242] shadow-[0_2px_8px_-2px_rgba(36,129,204,0.15)] dark:border dark:border-white/[0.08]">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-theme-text">
                            <span className="material-symbols-outlined text-[#2D62FF]">payments</span>
                            <span>{t('configureCreationPage.processingPriceLabel')}</span>
                          </div>
                          {isPriceFetching ? (
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                              <span className="text-theme-hint">{t('configureCreationPage.calculatingPriceStatus')}</span>
                            </div>
                          ) : calculatedPrice !== null ? (
                            <div className="flex items-center gap-1 font-medium">
                              <span className="text-lg">✨</span>
                              <span>{calculatedPrice}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-theme-hint">
                              <span className="text-lg">✨</span>
                              <span>{t('configureCreationPage.defaultPriceDisplay')}</span>
                            </div>
                          )}
                        </div>
                        
                        {userInfo.balance > 0 && (
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-theme-text">{t('configureCreationPage.yourBalanceLabel')}</span>
                            <div className="flex items-center gap-1 font-medium">
                              <span className="text-lg">✨</span>
                              <span>{userInfo.balance}</span>
                            </div>
                          </div>
                        )}
                        
                        {calculatedPrice !== null && userInfo.balance < calculatedPrice && (
                          <div className="flex items-center gap-2 text-amber-500 dark:text-amber-400 mt-2">
                            <span className="material-symbols-outlined">warning</span>
                            <span>{t('configureCreationPage.needsMoreSparklesText', { amount: calculatedPrice - userInfo.balance })}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Only show this message during upload */}
                  {isUploading && (
                    <>
                      <h4 className="text-xl font-medium mb-4">{t('uploadVideoPage.configureWhileWaitingHeader')}</h4>
                      <p className="text-sm text-gray-400 mb-6">{t('uploadVideoPage.configureWhileWaitingHint')}</p>
                    </>
                  )}
                  
                  {/* Auto-start processing checkbox - only shown during upload */}
                  {isUploading && (
                    <div className="flex items-center gap-2 mb-6 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg border border-blue-300 dark:border-blue-700">
                      <div className="w-5 h-5 mr-2 relative">
                        <input
                          type="checkbox"
                          id="auto-process"
                          checked={autoStartProcessing}
                          onChange={() => setAutoStartProcessing(!autoStartProcessing)}
                          disabled={!hasEnoughBalance}
                          className="sr-only peer"
                        />
                        <label 
                          htmlFor="auto-process" 
                          className={`flex items-center justify-center w-5 h-5 rounded peer-checked:bg-blue-600 bg-white dark:bg-[#2a3b4d] border border-gray-300 dark:border-white/[0.08] peer-checked:border-blue-600 ${!hasEnoughBalance ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {autoStartProcessing && (
                            <span className="material-symbols-outlined text-white text-xs">check</span>
                          )}
                        </label>
                      </div>
                      <label 
                        htmlFor="auto-process" 
                        className={`text-sm font-medium text-blue-900 dark:text-white ${!hasEnoughBalance ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span className="font-bold">{t('uploadVideoPage.autoStartCheckboxLabelPrefix')} {t('uploadVideoPage.autoStartCheckboxLabelSuffix')}</span>
                      </label>
                    </div>
                  )}
                  
                  {/* Configuration component - always shown */}
                  <div className="mb-6">
                    <BestMomentsCreationConfig 
                      config={bestMomentsConfig}
                      onConfigChange={handleConfigChange}
                    />
                  </div>
                  
                  {/* Start processing button - only shown after upload is complete and not auto-processing */}
                  {creationId && !isUploading && uploadedFileName && (
                    <button
                      onClick={async () => {
                        if (!hasEnoughBalance || !creationId || !bestMomentsConfig) return;
                        
                        try {
                          // Configure the creation
                          await ApiClient.configureBestMoments(creationId, {
                            captions_enabled: bestMomentsConfig.captions_enabled,
                            caption_config: bestMomentsConfig.caption_config
                          });
                          
                          // Start processing
                          await ApiClient.startProcessing(creationId);
                          
                          // PostHog: Capture processing started event
                          posthog.capture('creation_started', { 
                            creation_id: creationId, 
                            strategy_type: strategyType
                          });
                          
                          // Navigate to creation details page
                          navigate(`/creation/${creationId}`);
                        } catch (error) {
                          console.error("Error during manual processing:", error);
                        }
                      }}
                      className="w-full bg-[#2D62FF] text-white font-medium py-3 rounded-lg hover:bg-[#2D62FF]/90 transition-colors"
                    >
                      {t('uploadVideoPage.manualProcessingButton')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}